import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { sendMagicLink } from '../authService';
interface EmailSignupPromptProps {
  onDismiss: () => void;
}
export const EmailSignupPrompt: React.FC<EmailSignupPromptProps> = ({
  onDismiss
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      const success = await sendMagicLink(email);
      if (success) {
        setIsSuccess(true);
      } else {
        setError('Failed to send magic link. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <motion.div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} onClick={onDismiss} data-id="element-29">
      <motion.div className="bg-background-light border-2 border-gray-800 rounded-lg p-8 max-w-md w-full relative" initial={{
      scale: 0.9,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.9,
      opacity: 0
    }} onClick={e => e.stopPropagation()} data-id="element-30">
        <button onClick={onDismiss} className="absolute top-4 right-4 text-text-secondary hover:text-text-primary transition-colors" data-id="element-31">
          <XIcon className="h-5 w-5" data-id="element-32" />
        </button>
        <AnimatePresence mode="wait" data-id="element-33">
          {!isSuccess ? <motion.div key="signup-form" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} data-id="element-34">
              <div className="text-center mb-6" data-id="element-35">
                <motion.div className="inline-block text-5xl mb-4" animate={{
              rotate: [0, -10, 10, -10, 10, 0]
            }} transition={{
              duration: 0.5
            }} data-id="element-36">
                  💌
                </motion.div>
                <h2 className="text-2xl font-bold mb-2" data-id="element-37">
                  Want to keep the conversation going?
                </h2>
                <p className="text-text-secondary" data-id="element-38">
                  Sign up to get notified when people reply to your rants and
                  join the community.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-39">
                <div data-id="element-40">
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" className="w-full px-4 py-3 bg-background text-text-primary border border-gray-700 rounded-lg focus:outline-none focus:border-primary transition-all placeholder-text-secondary" disabled={isSubmitting} required data-id="element-41" />
                  {error && <p className="text-red-500 text-sm mt-2" data-id="element-42">{error}</p>}
                </div>
                <motion.button type="submit" className="w-full btn-primary py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} disabled={isSubmitting} data-id="element-43">
                  <MailIcon className="h-5 w-5" data-id="element-44" />
                  {isSubmitting ? 'Sending...' : 'Send Magic Link'}
                </motion.button>
                <button type="button" onClick={onDismiss} className="w-full text-text-secondary hover:text-text-primary transition-colors text-sm" data-id="element-45">
                  Maybe later
                </button>
              </form>
            </motion.div> : <motion.div key="success-message" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="text-center" data-id="element-46">
              <motion.div className="inline-block text-5xl mb-4" initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 10
          }} data-id="element-47">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" data-id="element-48" />
              </motion.div>
              <h2 className="text-2xl font-bold mb-2" data-id="element-49">Check your email!</h2>
              <p className="text-text-secondary mb-6" data-id="element-50">
                We've sent a magic link to <strong data-id="element-51">{email}</strong>. Click the
                link to sign in and continue the conversation.
              </p>
              <motion.button onClick={onDismiss} className="btn-primary py-3 px-6 rounded-lg font-semibold" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-52">
                Got it!
              </motion.button>
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
};